import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card, Button, Table, Form, Input, Select, Row, Col, Tag, Space,
  Typography, Divider, Statistic, Modal, message
} from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

const MOCK_DETAILS = {
  WO20260401001: {
    orderName: '摄像机Q1-批次001', orderId: 'WO20260401001',
    productName: '智能摄像机Q1', productId: 'PROD_Q1',
    tool: 'platform', authRule: 'HMY', authCount: 10000, hasThirdParty: true, createdAt: '2026-04-01 10:30:00',
    giftPackage: { name: '超高清录像 7天循环', id: 'PKG-7728-XA' },
    prodFwLabel: '主控固件 / V1.0.0',
    fwConfig: [
      { label: '硬件类型', value: '摇头机' },
      { label: '光源类型', value: '红外' },
      { label: '分辨率', value: '1080P' },
      { label: '语言', value: '中文' },
    ],
    testItems: ['图像测试', 'WIFI信号', '麦克风测试', '版本号测试'],
    otaTest: true, otaVersion: 'V1.1.0', otaFileName: 'main_fw_v1.1.0_ota.bin',
    group: '默认分组', writeMode: '工具写号',
    stats: { testDevices: 3500, passDevices: 3400, faultDevices: 100, totalTests: 7200, passTests: 7000, faultTests: 200 },
  },
  WO20260403005: {
    orderName: '报警器F1-外协', orderId: 'WO20260403005',
    productName: '烟雾报警器F1', productId: 'PROD_F1',
    tool: 'non-platform', authRule: 'HMY', authCount: 2000, hasThirdParty: false, createdAt: '2026-04-03 14:00:00', giftPackage: null,
    stats: { testDevices: 800, passDevices: 790, faultDevices: 10, totalTests: 1600, passTests: 1580, faultTests: 20 },
  },
  WO20260401002: {
    orderName: '摄像机Q1-批次002', orderId: 'WO20260401002',
    productName: '智能摄像机Q1', productId: 'PROD_Q1',
    tool: 'platform', authRule: 'HMY', authCount: 8000, hasThirdParty: true, createdAt: '2026-04-01 15:00:00', giftPackage: null,
    prodFwLabel: 'WiFi模组固件 / V1.2.0',
    fwConfig: [
      { label: '网口配置', value: '无线' },
      { label: '语言', value: '多语言' },
    ],
    testItems: ['图像测试', 'WIFI信号', '电池测试', '蓝牙测试'],
    otaTest: false,
    group: '默认分组', writeMode: '扫码枪',
    stats: { testDevices: 0, passDevices: 0, faultDevices: 0, totalTests: 0, passTests: 0, faultTests: 0 },
  },
};

const MOCK_LICENSES = [
  { key: '1', license: '010HMY260401A001', status: 'success', thirdParty: 'TP_001', updatedAt: '2026-04-05 08:30', auth_type: 'production' },
  { key: '2', license: '010HMY260401A002', status: 'success', thirdParty: 'TP_002', updatedAt: '2026-04-05 09:10', auth_type: 'production' },
  { key: '3', license: '010HMY260401A003', status: 'burning', thirdParty: 'TP_003', updatedAt: '2026-04-05 09:15', auth_type: 'production' },
  { key: '4', license: '010HMY260401A004', status: 'fail', thirdParty: 'TP_004', updatedAt: '2026-04-05 09:20', auth_type: 'production' },
  { key: '5', license: '010HMY260401A005', status: 'pending', thirdParty: null, updatedAt: '2026-04-01 10:30', auth_type: 'test' },
];

const LICENSE_STATUS = {
  pending: { text: '待使用', color: 'default' },
  burning: { text: '烧录中', color: 'processing' },
  success: { text: '烧录成功', color: 'success' },
  fail: { text: '烧录失败', color: 'error' },
};

const pct = (a, b) => b > 0 ? `${(a / b * 100).toFixed(1)}%` : '0%';

const OrderDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('id');
  const detail = MOCK_DETAILS[orderId] || MOCK_DETAILS['WO20260401001'];
  const s = detail.stats;
  const isPlatform = detail.tool === 'platform';
  const [form] = Form.useForm();
  const [fwConfigVisible, setFwConfigVisible] = useState(false);

  const licenseColumns = [
    { title: 'License', dataIndex: 'license', width: 220 },
    { title: '生产状态', dataIndex: 'status', width: 120, render: (v) => { const st = LICENSE_STATUS[v]; return <Tag color={st.color}>{st.text}</Tag>; } },
    { title: '三方授权', dataIndex: 'thirdParty', width: 150, render: (v) => v || '——' },
    { title: '更新时间', dataIndex: 'updatedAt', width: 180 },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => navigate('/production/productionOrder')} />
        <Title level={4} style={{ margin: 0 }}>工单详情</Title>
        <Text type="secondary">{detail.orderId}</Text>
      </div>

      <Card bordered={false} style={{ maxWidth: 960, marginBottom: 16 }}>
        {/* 基本信息 */}
        <Divider orientation="left" plain>基本信息</Divider>
        <Row gutter={24}>
          <Col span={8}><div style={{ marginBottom: 16 }}><Text type="secondary">工单名称</Text><br /><Text>{detail.orderName}</Text></div></Col>
          <Col span={8}><div style={{ marginBottom: 16 }}><Text type="secondary">生产工具</Text><br /><Text>{isPlatform ? '平台产测' : '非平台产测'}</Text></div></Col>
          <Col span={8}><div style={{ marginBottom: 16 }}><Text type="secondary">生产产品</Text><br /><Text>{detail.productName}</Text><br /><Text type="secondary" style={{ fontSize: 12 }}>{detail.productId}</Text></div></Col>
        </Row>
        <Row gutter={24}>
          <Col span={8}><div style={{ marginBottom: 16 }}><Text type="secondary">创建时间</Text><br /><Text>{detail.createdAt}</Text></div></Col>
          {isPlatform && (
            <Col span={8}>
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary">赠送套餐</Text><br />
                {detail.giftPackage ? (
                  <div><Text strong style={{ color: '#52c41a' }}>已配置</Text><br /><Text type="secondary" style={{ fontSize: 12 }}>{detail.giftPackage.name}（{detail.giftPackage.id}）</Text></div>
                ) : (
                  <Text type="secondary">未配置</Text>
                )}
              </div>
            </Col>
          )}
        </Row>

        {/* 生产授权 */}
        <Divider orientation="left" plain>生产授权</Divider>
        <Row gutter={24}>
          <Col span={8}><div style={{ marginBottom: 16 }}><Text type="secondary">生成规则</Text><br /><Text>{detail.authRule}</Text></div></Col>
          <Col span={8}><div style={{ marginBottom: 16 }}><Text type="secondary">生产数量</Text><br /><Text>{detail.authCount.toLocaleString()} 个</Text></div></Col>
          <Col span={8}><div style={{ marginBottom: 16 }}><Text type="secondary">三方授权</Text><br />{detail.hasThirdParty ? <Button type="link" icon={<DownloadOutlined />} style={{ padding: 0 }} onClick={() => message.success('下载成功')}>下载</Button> : <Text type="secondary">——</Text>}</div></Col>
        </Row>

        {isPlatform && (
          <>
            {/* 产测配置 */}
            <Divider orientation="left" plain>产测配置</Divider>
            <Row gutter={24}>
              <Col span={12}><div style={{ marginBottom: 16 }}><Text type="secondary">生产固件</Text><br /><Text>{detail.prodFwLabel}</Text></div></Col>
              {detail.fwConfig && detail.fwConfig.length > 0 && (
                <Col span={12}>
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">固件配置</Text><br />
                    <Text type="secondary" style={{ fontSize: 12 }}>固件TF卡配置</Text><br />
                    <Button type="link" style={{ padding: 0 }} onClick={() => setFwConfigVisible(true)}>查看配置</Button>
                  </div>
                </Col>
              )}
            </Row>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">生产测试项</Text><br />
              <Space size={[4, 8]} wrap style={{ marginTop: 4 }}>{detail.testItems?.map(item => <Tag key={item}>{item}</Tag>)}</Space>
              {detail.testItems?.includes('版本号测试') && <div style={{ marginTop: 4 }}><Text type="secondary" style={{ fontSize: 12 }}>产测工具检测设备固件与生产固件版本是否一致</Text></div>}
            </div>
            {/* 固件升级 */}
            {detail.otaTest && (
              <>
                <Divider orientation="left" plain>固件升级</Divider>
                <div style={{ marginBottom: 16 }}>
                  <Text>是</Text><br />
                  <Text type="secondary" style={{ fontSize: 12 }}>产测工具会进行强制升级</Text>
                </div>
              </>
            )}

            {/* 其他配置 */}
            <Divider orientation="left" plain>其他配置</Divider>
            <Row gutter={24}>
              <Col span={8}><div style={{ marginBottom: 16 }}><Text type="secondary">产测分组</Text><br /><Text>{detail.group}</Text></div></Col>
              <Col span={8}><div style={{ marginBottom: 16 }}><Text type="secondary">写号方式</Text><br /><Text>{detail.writeMode}</Text></div></Col>
            </Row>
          </>
        )}
      </Card>

      {/* 测试统计 */}
      <Card bordered={false} title="测试统计" style={{ marginBottom: 16 }}>
        <Row gutter={[24, 16]}>
          <Col span={4}><Statistic title="测试设备" value={s.testDevices} suffix="台" /></Col>
          <Col span={4}><Statistic title="通过设备" value={s.passDevices} suffix="台" /></Col>
          <Col span={4}><Statistic title="设备通过率" value={pct(s.passDevices, s.testDevices)} /></Col>
          <Col span={4}><Statistic title="故障设备" value={s.faultDevices} suffix="台" /></Col>
          <Col span={4}><Statistic title="设备故障率" value={pct(s.faultDevices, s.testDevices)} /></Col>
        </Row>
        <Divider style={{ margin: '16px 0' }} />
        <Row gutter={[24, 16]}>
          <Col span={4}><Statistic title="测试总数" value={s.totalTests} suffix="次" /></Col>
          <Col span={4}><Statistic title="通过次数" value={s.passTests} suffix="次" /></Col>
          <Col span={4}><Statistic title="通过率" value={pct(s.passTests, s.totalTests)} /></Col>
          <Col span={4}><Statistic title="故障次数" value={s.faultTests} suffix="次" /></Col>
          <Col span={4}><Statistic title="故障率" value={pct(s.faultTests, s.totalTests)} /></Col>
        </Row>
      </Card>

      {/* 生产授权码 */}
      <Card bordered={false} title="生产授权码">
        <Form form={form} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item name="license">
            <Input placeholder="License" allowClear style={{ width: 220 }} />
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="生产状态" allowClear style={{ width: 140 }}>
              {Object.entries(LICENSE_STATUS).map(([k, v]) => <Option key={k} value={k}>{v.text}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item><Button type="primary" icon={<SearchOutlined />}>查询</Button></Form.Item>
          <Form.Item><Button icon={<ReloadOutlined />} onClick={() => form.resetFields()}>重置</Button></Form.Item>
          <Form.Item><Button icon={<DownloadOutlined />} onClick={() => message.success('导出成功')}>导出</Button></Form.Item>
        </Form>
        <Table columns={licenseColumns} dataSource={MOCK_LICENSES} rowKey="key" pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }} />
      </Card>

      {/* 固件配置 - 只读弹窗 */}
      <Modal title="固件配置" open={fwConfigVisible} onCancel={() => setFwConfigVisible(false)} destroyOnClose width={600}
        footer={<Button onClick={() => setFwConfigVisible(false)}>关闭</Button>}>
        {detail.fwConfig && (
          <div style={{ marginTop: 8 }}>
            {detail.fwConfig.map((item, i) => (
              <div key={i} style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <Text type="secondary" style={{ width: 180, flexShrink: 0 }}>{item.label}</Text>
                <Text>{item.value}</Text>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OrderDetail;
